/**
 * ShareModal — Card selection, preview, and download modal.
 *
 * Offers three card types: Trajectory, Quest Achievement, and Streak.
 * Renders a live preview and provides Download (PNG) and Copy (text) actions.
 *
 * Opened via a "Share" button on the Overview page.
 * Self-contained — reads all data from useApp() and shareEngine.
 */
import { useState, useMemo, useCallback } from "react";
import { useApp } from '../../hooks/useApp';
import { QUESTS } from '../quests/questConfig';
import {
  trajectoryCardData,
  questCardData,
  streakCardData,
  renderCardToBlob,
  downloadCard,
} from './shareEngine';

/* ─── Card type options ─── */
const CARD_TYPES = [
  { id: "trajectory", label: "Wealth Plan", icon: "📈", desc: "Your growth trajectory" },
  { id: "quest", label: "Achievement", icon: "🏆", desc: "Quest completion" },
  { id: "streak", label: "Streak", icon: "🔥", desc: "Consistency record" },
];

export default function ShareModal({ open, onClose }) {
  const { s, c, proj, pF, fiAge, fiReached, showToast } = useApp();
  const [cardType, setCardType] = useState("trajectory");
  const [selectedQuest, setSelectedQuest] = useState(null);

  // Completed quests for the quest card picker
  const completedQuests = useMemo(() => {
    const done = s.questsCompleted || {};
    return QUESTS.filter((q) => q.id in done);
  }, [s.questsCompleted]);

  // Auto-select first completed quest
  const activeQuest = selectedQuest || (completedQuests.length > 0 ? completedQuests[0].id : null);

  // Build card data
  const cardData = useMemo(() => {
    if (cardType === "trajectory") {
      return trajectoryCardData(s, proj, pF, fiAge, fiReached);
    }
    if (cardType === "quest" && activeQuest) {
      return questCardData(activeQuest, s);
    }
    if (cardType === "streak") {
      return streakCardData(s);
    }
    return null;
  }, [cardType, activeQuest, s, proj, pF, fiAge, fiReached]);

  // Generate preview image
  const previewUrl = useMemo(() => {
    if (!cardData) return null;
    return renderCardToBlob(cardData, s.dark);
  }, [cardData, s.dark]);

  // Download handler
  const handleDownload = useCallback(() => {
    if (!previewUrl) return;
    const names = {
      trajectory: "wealthmaxx-plan",
      quest: `wealthmaxx-${activeQuest || "quest"}`,
      streak: "wealthmaxx-streak",
    };
    downloadCard(previewUrl, `${names[cardType] || "wealthmaxx"}.png`);
    showToast("Card downloaded!", "success");
  }, [previewUrl, cardType, activeQuest, showToast]);

  // Copy text summary
  const handleCopy = useCallback(async () => {
    let text = "";
    if (cardType === "trajectory" && cardData) {
      text = `${cardData.headline}\n${cardData.stats.map((s) => `${s.label}: ${s.value}`).join(" · ")}\n${cardData.tagline}\n\n— Wealthmaxxing Plan OS`;
    } else if (cardType === "quest" && cardData) {
      text = `🏆 ${cardData.title}\n"${cardData.reward}"\n\n— Wealthmaxxing Plan OS`;
    } else if (cardType === "streak" && cardData) {
      text = `${cardData.emoji} ${cardData.count}-Day Streak\n${cardData.tagline}\n\n— Wealthmaxxing Plan OS`;
    }
    try {
      await navigator.clipboard.writeText(text);
      showToast("Copied to clipboard!", "success");
    } catch {
      showToast("Copy failed — try downloading instead", "error");
    }
  }, [cardType, cardData, showToast]);

  if (!open) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 100,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "absolute", inset: 0,
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        }}
      />

      {/* Modal */}
      <div style={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: 540,
        background: s.dark ? "#1C1C1E" : "#FFFFFF",
        borderRadius: 20,
        border: `1px solid ${s.dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        boxShadow: "0 24px 80px rgba(0,0,0,0.3)",
        padding: "28px 24px",
        maxHeight: "90vh",
        overflowY: "auto",
        animation: "fadeSlideUp 0.3s cubic-bezier(0.25,0.1,0.25,1)",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 24,
        }}>
          <div>
            <div style={{
              fontSize: 20, fontWeight: 800, color: c.fg,
              letterSpacing: "-0.03em",
            }}>
              Share Your Progress
            </div>
            <div style={{ fontSize: 13, color: c.muted, marginTop: 4 }}>
              Status-positive. Never shows balances.
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 16,
              border: "none",
              background: s.dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
              color: c.muted, fontSize: 16, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        {/* Card type selector */}
        <div style={{
          display: "flex", gap: 8, marginBottom: 20,
        }}>
          {CARD_TYPES.map((ct) => {
            const active = cardType === ct.id;
            const disabled = ct.id === "quest" && completedQuests.length === 0;
            return (
              <button
                key={ct.id}
                onClick={() => !disabled && setCardType(ct.id)}
                style={{
                  flex: 1, padding: "12px 8px", borderRadius: 12,
                  border: `1.5px solid ${active ? c.blue : "transparent"}`,
                  background: active
                    ? (s.dark ? "rgba(10,132,255,0.10)" : "rgba(10,132,255,0.05)")
                    : (s.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.35 : 1,
                  textAlign: "center",
                  transition: "all 0.2s ease",
                }}
              >
                <div style={{ fontSize: 20, marginBottom: 4 }}>{ct.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: active ? c.blue : c.fg }}>
                  {ct.label}
                </div>
                <div style={{ fontSize: 10, color: c.muted, marginTop: 2 }}>
                  {ct.desc}
                </div>
              </button>
            );
          })}
        </div>

        {/* Quest picker (only for quest card type) */}
        {cardType === "quest" && completedQuests.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: c.muted,
              letterSpacing: "0.06em", textTransform: "uppercase",
              marginBottom: 8,
            }}>
              Choose Achievement
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {completedQuests.map((q) => (
                <button
                  key={q.id}
                  onClick={() => setSelectedQuest(q.id)}
                  style={{
                    padding: "6px 14px", borderRadius: 10,
                    border: `1px solid ${activeQuest === q.id ? c.blue : c.border}`,
                    background: activeQuest === q.id
                      ? (s.dark ? "rgba(10,132,255,0.10)" : "rgba(10,132,255,0.05)")
                      : "transparent",
                    color: activeQuest === q.id ? c.blue : c.muted,
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 5,
                    transition: "all 0.15s ease",
                  }}
                >
                  {q.icon} {q.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Preview */}
        {previewUrl ? (
          <div style={{
            borderRadius: 14, overflow: "hidden",
            marginBottom: 20,
            border: `1px solid ${s.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
          }}>
            <img
              src={previewUrl}
              alt="Share card preview"
              style={{ width: "100%", display: "block" }}
            />
          </div>
        ) : (
          <div style={{
            padding: "40px 20px", textAlign: "center",
            borderRadius: 14, marginBottom: 20,
            background: s.dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
            fontSize: 13, color: c.muted,
          }}>
            {cardType === "quest"
              ? "Complete a quest first to share an achievement card."
              : "Card preview unavailable."
            }
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={handleDownload}
            disabled={!previewUrl}
            style={{
              flex: 1, padding: "13px 20px", borderRadius: 12,
              border: "none", background: c.blue, color: "#fff",
              fontSize: 14, fontWeight: 700, cursor: previewUrl ? "pointer" : "not-allowed",
              opacity: previewUrl ? 1 : 0.4,
              boxShadow: `0 2px 12px ${c.blue}30`,
              transition: "all 0.2s ease",
            }}
          >
            ⬇ Download PNG
          </button>
          <button
            onClick={handleCopy}
            disabled={!cardData}
            style={{
              flex: 1, padding: "13px 20px", borderRadius: 12,
              border: `1px solid ${c.border}`,
              background: "transparent", color: c.fg,
              fontSize: 14, fontWeight: 600, cursor: cardData ? "pointer" : "not-allowed",
              opacity: cardData ? 1 : 0.4,
              transition: "all 0.2s ease",
            }}
          >
            📋 Copy Text
          </button>
        </div>

        {/* Privacy note */}
        <div style={{
          marginTop: 16, fontSize: 11, color: c.muted, opacity: 0.5,
          textAlign: "center", lineHeight: 1.5,
        }}>
          Cards share momentum and milestones — never your actual balances.
        </div>
      </div>
    </div>
  );
}
